/** @jsxImportSource theme-ui */
import { FormikProps } from 'formik';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { Button, Input, ModalContentProps, SemanticShorthandItem, Table } from 'semantic-ui-react';

import { applySearchOnRecord } from 'utils/searchTableHelper';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Loading } from 'components/Loading';

import { BdConfirm } from './BdConfirm';
import { CustomCrudModal } from './CustomCrudModal';

type SimpleCrudTableProps<T, FM> = {
  loading?: boolean;
  entityType?: string;
  list: T[];
  columns: {
    name: string;
    getter: (item: T) => any;
  }[];
  createNew?: {
    title?: string;
    renderForm: (props: FormikProps<FM>) => React.ReactElement;
    onSubmitForm: (data: FM) => Promise<void> | void;
    validationSchema?: any;
    seed?: Partial<FM>;
  };
  exportToCsv?: {
    title?: string;
    isDisabled?: boolean;
    onExport: () => Promise<void>;
  };
  actions?: {
    type: 'confirm' | 'custom';
    actionText: ((item: T) => string) | string;
    confirm?: {
      render: (item: T) => SemanticShorthandItem<ModalContentProps>;
      onSubmit: (item: T) => void;
    };
    custom?: {
      isDisabled?: (data: T) => boolean;
      renderTitle?: (data: T) => string;
      renderForm: (props: FormikProps<FM>) => React.ReactElement;
      onSubmitForm: (data: FM) => Promise<void> | void;
      validationSchema?: any;
      mapToFormData?: (item: T) => FM;
    };
  }[];
  keyGetter: (item: T) => string;
  rowDisabledGetter?: (item: T) => boolean;
};

export function SimpleCrudTable<T, FM>({
  loading = false,
  entityType = '...',
  list,
  columns,
  createNew,
  exportToCsv,
  actions,
  keyGetter,
  rowDisabledGetter,
}: React.PropsWithChildren<SimpleCrudTableProps<T, FM>>): React.ReactElement {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = React.useState([]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useMemo(() => {
    setSearchResults(list);
    setSearchText('');
  }, [list]);

  useMemo(() => {
    const results = !searchText ? list : list.filter(r => applySearchOnRecord(searchText, r));
    setSearchResults(results);
  }, [searchText]);

  return (
    <>
      <FlexGroup
        suffix={
          <Input
            icon={<BdIcon name="icon-search" color="sea100" bold />}
            iconPosition="left"
            placeholder={`Search ${entityType}`}
            sx={{ input: { height: '2.65rem' } }}
            value={searchText}
            disabled={loading}
            onChange={e => handleSearch(e)}
            loading={loading}
          />
        }
        alignItems="end"
      >
        {createNew && (
          <CustomCrudModal
            disabled={false}
            title={createNew.title || 'Create new'}
            actionText="Create New"
            seed={createNew.seed}
            validationSchema={createNew.validationSchema}
            renderForm={createNew.renderForm}
            onSubmitForm={createNew.onSubmitForm}
          />
        )}
        {exportToCsv && (
          <Button disabled={exportToCsv.isDisabled} onClick={exportToCsv.onExport}>
            {exportToCsv.title || 'Export to csv'}
          </Button>
        )}
      </FlexGroup>
      {loading && <Loading />}
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            {columns.map(({ name }) => (
              <Table.HeaderCell key={name}>{name}</Table.HeaderCell>
            ))}
            {actions && <Table.HeaderCell />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {searchResults &&
            searchResults.map(item => (
              <Table.Row key={keyGetter(item)}>
                {columns.map(({ name: columnName, getter }) => (
                  <Table.Cell
                    key={`${keyGetter(item)}:${columnName}`}
                    disabled={rowDisabledGetter && rowDisabledGetter(item)}
                  >
                    {getter(item)}
                  </Table.Cell>
                ))}
                {actions && (
                  <Table.Cell
                    textAlign="right"
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {actions.map(({ type, actionText, confirm, custom }) => (
                      <React.Fragment key={`${keyGetter(item)}:__actions__:${actionText}`}>
                        {type === 'custom' && custom && (
                          <CustomCrudModal
                            disabled={custom.isDisabled ? custom.isDisabled(item) : false}
                            title={custom.renderTitle ? custom.renderTitle(item) : 'Edit'}
                            seed={custom.mapToFormData && custom.mapToFormData(item)}
                            validationSchema={custom.validationSchema}
                            renderForm={custom.renderForm}
                            onSubmitForm={custom.onSubmitForm}
                          />
                        )}
                        {type === 'confirm' && confirm && (
                          <BdConfirm
                            actionText={
                              typeof actionText === 'function' ? actionText(item) : actionText
                            }
                            content={confirm.render(item)}
                            callback={() => confirm.onSubmit(item)}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
    </>
  );
}
